const chapter = {
  id: 'states-of-matter',
  slug: 'states-of-matter',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'States of Matter — Gases & Liquids',
        hindi: 'द्रव्य की अवस्थाएँ — गैस और द्रव',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 1-2 प्रश्न',
  },
  syllabusTags: ['Physical Chemistry', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'intermolecular',
      title: {
        english: 'Intermolecular Forces',
        hindi: 'अन्तराण्विक बल',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Dispersion (London) forces** — present in all molecules; due to instantaneous dipoles. Strength rises with molecular size / polarisability.',
        hindi: '**फैलाव (लंदन) बल** - सभी अणुओं में मौजूद; तात्क्षणिक द्विध्रुव के कारण. आणविक आकार/ध्रुवीकरण के साथ ताकत बढ़ती है।',
            },
            {
              english: '**Dipole–dipole** — between polar molecules (HCl, SO₂).',
        hindi: '**द्विध्रुव-द्विध्रुव** - ध्रुवीय अणुओं (HCl, SO₂) के बीच।',
            },
            {
              english: '**Dipole–induced dipole** — polar molecule polarises a non-polar one.',
        hindi: '**द्विध्रुव-प्रेरित द्विध्रुव** - ध्रुवीय अणु एक गैर-ध्रुवीय को ध्रुवीकृत करता है।',
            },
            {
              english: '**Hydrogen bonding** — special, strong dipole–dipole when H is bound to N/O/F.',
        hindi: '**हाइड्रोजन बंधन** - विशेष, मजबूत द्विध्रुव-द्विध्रुव जब H N/O/F से बंधा होता है।',
            },
            {
              english: '**Ion–dipole** — between ion and polar solvent (Na⁺ in water).',
        hindi: '**आयन-द्विध्रुव** - आयन और ध्रुवीय विलायक (पानी में Na⁺) के बीच।',
            },
          ],
        },
        {
          type: 'note',
          text: {
            english: 'Thermal energy ($\\propto T$) opposes intermolecular attractions. State of matter is the balance between the two.',
        hindi: 'तापीय ऊर्जा ($\\propto T$) अंतरआण्विक आकर्षण का विरोध करती है। पदार्थ की स्थिति दोनों के बीच संतुलन है।',
          },
        },
      ],
    },
    {
      id: 'gas-laws',
      title: {
        english: 'Ideal Gas Laws',
        hindi: 'आदर्श गैस नियम',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Law', 'Statement', 'Equation', 'Conditions fixed'],
          rows: [
            ['Boyle', '$V \\propto 1/P$', '$PV = $ const', '$n, T$'],
            ['Charles', '$V \\propto T$', '$V/T = $ const', '$n, P$'],
            ['Gay-Lussac', '$P \\propto T$', '$P/T = $ const', '$n, V$'],
            ['Avogadro', '$V \\propto n$', '$V/n = $ const', '$P, T$'],
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Ideal gas equation',
        hindi: 'आदर्श गैस समीकरण',
          },
          statement: {
            english: `$$PV = nRT = \\dfrac{w}{M}RT \\Rightarrow PM = dRT$$
where $d$ = density. $R = 8.314$ J/(mol·K) $= 0.0821$ L·atm/(mol·K) $= 1.987$ cal/(mol·K).`,
            hindi: '$$PV = nRT = \\dfrac{w}{M}RT \\Rightarrow PM = dRT$$ जहां $d$ = घनत्व। $R = 8.314$ J/(mol·K) $= 0.0821$ L·atm/(mol·K) $= 1.987$ cal/(mol·K)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Standard conditions: **STP** (IUPAC, 0 °C, 1 bar) ⇒ molar volume $= 22.7$ L. Older STP (0 °C, 1 atm) ⇒ $22.4$ L (still used in NCERT problems).',
        hindi: 'मानक स्थितियाँ: **STP** (IUPAC, 0 °C, 1__ बार) ⇒ मोलर वॉल्यूम $= 22.7$ L. पुराना STP (0 °C, 1 atm) ⇒ $22.4$ L (अभी भी NCERT समस्याओं में उपयोग किया जाता है)।',
            },
            {
              english: '**Combined gas law**: $P_1V_1/T_1 = P_2V_2/T_2$.',
        hindi: '**संयुक्त गैस कानून**: $P_1V_1/T_1 = P_2V_2/T_2$।',
            },
            {
              english: '**Dalton\'s law of partial pressures**: $P_{\\text{tot}} = \\sum P_i$, $P_i = x_i P_{\\text{tot}}$.',
        hindi: '**डाल्टन का आंशिक दबाव का नियम**: $P_{\\text{tot}} = \\sum P_i$, $P_i = x_i P_{\\text{tot}}$।',
            },
            {
              english: '**Graham\'s law of diffusion**: rate $r \\propto 1/\\sqrt M$ (at same $P, T$). $r_1/r_2 = \\sqrt{M_2/M_1}$.',
        hindi: '**ग्राहम के प्रसार का नियम**: दर $r \\propto 1/\\sqrt M$ (उसी $P, T$ पर)। $r_1/r_2 = \\sqrt{M_2/M_1}$.',
            },
            {
              english: '**Aqueous tension**: when gas collected over water, $P_{\\text{dry gas}} = P_{\\text{total}} - P_{\\text{water}}$.',
        hindi: '**जलीय तनाव**: जब गैस पानी के ऊपर एकत्र होती है, $P_{\\text{सूखी गैस}} = P_{\\text{कुल}} - P_{\\text{पानी}}$।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: '5 g of N₂ at 300 K, 2 atm. Volume?',
        hindi: '300 K पर 5 ग्राम N₂, 2 एटीएम। आयतन?',
          },
          solution: {
            english: '$n = 5/28 = 0.179$ mol. $V = nRT/P = 0.179 \\cdot 0.0821 \\cdot 300/2 = 2.2$ L.',
        hindi: '$n = 5/28 = 0.179$ मोल। $V = nRT/P = 0.179 \\cdot 0.0821 \\cdot 300/2 = 2.2$ L.',
          },
        },
      ],
    },
    {
      id: 'kinetic-theory',
      title: {
        english: 'Kinetic Molecular Theory & Molecular Speeds',
        hindi: 'गतिज आण्विक सिद्धान्त और आण्विक चालें',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Postulates: gas = large number of tiny particles, negligible volume, no inter-particle force, elastic collisions, $KE \\propto T$.',
        hindi: 'अभिधारणा: गैस = बड़ी संख्या में छोटे कण, नगण्य आयतन, कोई अंतर-कण बल नहीं, लोचदार टकराव, $KE \\propto T$।',
            },
            {
              english: '**Average kinetic energy** per molecule: $\\bar E = \\tfrac32 k_B T$. Per mole: $\\tfrac32 RT$.',
        hindi: '**औसत गतिज ऊर्जा** प्रति अणु: $\\bar E = \\tfrac32 k_B T$. प्रति मोल: $\\tfrac32 RT$.',
            },
            {
              english: 'Pressure: $P = \\tfrac13 \\rho \\bar v^2 = \\tfrac{nM\\bar v^2}{3V}$.',
        hindi: 'दबाव: $P = \\tfrac13 \\rho \\bar v^2 = \\tfrac{nM\\bar v^2}{3V}$.',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Three molecular speeds',
        hindi: 'तीन आणविक गति **मैक्सवेल-बोल्ट्ज़मैन वितरण**: शिखर () के बढ़ने पर दाईं ओर स्थानांतरित हो जाता है; चपटा (अधिक फैलाव)। बड़े के लिए समान पर, शीर्ष बदलाव बाईं ओर हैं।',
          },
          statement: {
            english: `$$v_{rms} = \\sqrt{\\dfrac{3RT}{M}},\\quad v_{avg} = \\sqrt{\\dfrac{8RT}{\\pi M}},\\quad v_{mp} = \\sqrt{\\dfrac{2RT}{M}}.$$
Ratio: $v_{mp} : v_{avg} : v_{rms} = 1 : 1.128 : 1.224$.`,
            hindi: '$$v_{rms} = \\sqrt{\\dfrac{3RT}{M}},\\quad v_{avg} = \\sqrt{\\dfrac{8RT}{\\pi M}},\\quad v_{mp} = \\sqrt{\\dfrac{2RT}{M}}.$$ अनुपात: $v_{mp} : v_{avg} : v_{rms} = 1 : 1.128 : 1.224$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Maxwell–Boltzmann distribution**: peak ($v_{mp}$) shifts right as $T$ rises; flattens (more spread). For larger $M$ at same $T$, peak shifts left.',
        hindi: '**मैक्सवेल-बोल्ट्ज़मैन वितरण**: शिखर ($v_{mp}$) $T$ के बढ़ने पर दाईं ओर स्थानांतरित हो जाता है; चपटा (अधिक फैलाव)। बड़े $M$ के लिए समान $T$ पर, शीर्ष बदलाव बाईं ओर हैं।',
            },
            {
              english: 'Use $M$ in **kg/mol** when $R$ in J/(mol·K) for SI speeds (m/s).',
        hindi: 'SI गति (m/s) के लिए J/(mol·K) में $R$ होने पर **kg/mol** में $M$ का उपयोग करें।',
            },
          ],
        },
      ],
    },
    {
      id: 'real-gases',
      title: {
        english: 'Real Gases & van der Waals Equation',
        hindi: 'वास्तविक गैसें और वान डेर वाल्स समीकरण',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Real gases deviate from ideal behaviour at high pressure and low temperature. Two corrections needed: molecular **volume** is not negligible, and there are intermolecular **attractions** that reduce wall pressure.',
        hindi: 'वास्तविक गैसें उच्च दबाव और कम तापमान पर आदर्श व्यवहार से विचलित हो जाती हैं। दो सुधारों की आवश्यकता है: आणविक **आयतन** नगण्य नहीं है, और अंतर-आणविक **आकर्षण** हैं जो दीवार के दबाव को कम करते हैं।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'van der Waals equation',
        hindi: 'वैन डेर वाल्स समीकरण',
          },
          statement: {
            english: `$$\\left(P + \\dfrac{a n^2}{V^2}\\right)(V - nb) = nRT.$$
$a$ = measure of intermolecular attraction; $b$ = excluded volume per mole (~4× actual molecular volume).`,
            hindi: '$$\\left(P + \\dfrac{a n^2}{V^2}\\right)(V - nb) = nRT.$$ $a$ = अंतरआण्विक आकर्षण का माप; $b$ = प्रति मोल अपवर्जित आयतन (~4× वास्तविक आणविक आयतन)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Compressibility factor** $Z = PV/(nRT)$. $Z = 1$ for ideal; $Z < 1$ at low $P$ (attractions dominate); $Z > 1$ at high $P$ (repulsions / size dominate).',
        hindi: '**संपीड़न कारक** $Z = PV/(nRT)$। आदर्श के लिए $Z = 1$; $Z < 1$ कम $P$ पर (आकर्षण हावी); $Z > 1$ उच्च $P$ पर (प्रतिकर्षण/आकार हावी)।',
            },
            {
              english: '**Boyle temperature** $T_B = a/(Rb)$ — at this $T$ a real gas behaves ideally over a wide range of $P$.',
        hindi: '**बॉयल तापमान** $T_B = a/(Rb)$ - इस $T$ पर एक वास्तविक गैस $P$ की विस्तृत श्रृंखला में आदर्श व्यवहार करती है।',
            },
            {
              english: '**Critical constants** (point at which liquid-vapour distinction vanishes):',
        hindi: '**महत्वपूर्ण स्थिरांक** (वह बिंदु जिस पर तरल-वाष्प भेद गायब हो जाता है):',
            },
            {
              english: '$T_c = 8a/(27Rb)$, $P_c = a/(27b^2)$, $V_c = 3b$, $Z_c = 3/8 = 0.375$.',
        hindi: '$T_c = 8a/(27Rb)$, $P_c = a/(27b^2)$, $V_c = 3b$, $Z_c = 3/8 = 0.375$।',
            },
            {
              english: 'Liquefaction is easier for gases with large $a$ (e.g. NH₃, CO₂); H₂ and He need very low $T$.',
        hindi: 'बड़ी $a$ (जैसे NH₃, CO₂) वाली गैसों के लिए द्रवीकरण आसान है; H₂ और उसे बहुत कम $T$ की आवश्यकता है।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Ratio $v_{rms}$ of O₂ to H₂ at the same temperature?',
        hindi: 'एक ही तापमान पर O₂ से H₂ का अनुपात $v_{rms}$?',
          },
          solution: {
            english: '$v_{rms} \\propto 1/\\sqrt M$. Ratio $= \\sqrt{M_{H_2}/M_{O_2}} = \\sqrt{2/32} = 1/4$.',
        hindi: '$v_{rms} \\propto 1/\\sqrt M$. अनुपात $= \\sqrt{M_{H_2}/M_{O_2}} = \\sqrt{2/32} = 1/4$.',
          },
        },
      ],
    },
    {
      id: 'liquids',
      title: {
        english: 'Liquids — Surface Tension, Viscosity, Vapour Pressure',
        hindi: 'द्रव — पृष्ठ तनाव, श्यानता, वाष्प दाब',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Vapour pressure** — pressure exerted by vapour in equilibrium with its liquid (closed container). Increases with $T$. At $T$ where vapour pressure = atmospheric, liquid **boils**.',
        hindi: '**वाष्प दबाव** - अपने तरल (बंद कंटेनर) के साथ संतुलन में वाष्प द्वारा लगाया गया दबाव। $T$ के साथ बढ़ता है। $T$ पर जहां वाष्प pressure = atmospheric होता है, तरल **उबलता है**।',
            },
            {
              english: 'Clausius–Clapeyron: $\\ln(P_2/P_1) = -\\dfrac{\\Delta H_{\\text{vap}}}{R}\\left(\\dfrac{1}{T_2} - \\dfrac{1}{T_1}\\right)$.',
        hindi: 'क्लॉसियस-क्लैपेरॉन: $\\ln(P_2/P_1) = -\\dfrac{\\Delta H_{\\text{vap}}}{R}\\left(\\dfrac{1}{T_2} - \\dfrac{1}{T_1}\\right)$।',
            },
            {
              english: '**Surface tension** $\\gamma$ (N/m or J/m²): force per unit length across surface. Caused by unbalanced inward force on surface molecules. Decreases with $T$.',
        hindi: '**सतह तनाव** $\\gamma$ (N/m या J/m²): सतह पर प्रति इकाई लंबाई पर बल। सतह के अणुओं पर असंतुलित आंतरिक बल के कारण होता है। $T$ से घटता है।',
            },
            {
              english: 'Capillary rise: $h = 2\\gamma\\cos\\theta/(\\rho g r)$.',
        hindi: 'केशिका वृद्धि: $h = 2\\gamma\\cos\\theta/(\\rho g r)$। **चिपचिपापन** $h = 2\\gamma\\cos\\theta/(\\rho g r)$ (Pa·s): प्रवाह का प्रतिरोध। (गैसों के विपरीत) के साथ तेजी से घटता है। ग्रेटर एच-बॉन्डिंग ⇒ अधिक (जैसे ग्लिसरॉल, शहद)।',
            },
            {
              english: '**Viscosity** $\\eta$ (Pa·s): resistance to flow. Decreases sharply with $T$ (unlike gases). Greater H-bonding ⇒ greater $\\eta$ (e.g. glycerol, honey).',
        hindi: '**चिपचिपाहट** $\\eta$ (Pa·s): प्रवाह का प्रतिरोध। $T$ (गैसों के विपरीत) के साथ तेजी से घटता है। ग्रेटर एच-बॉन्डिंग ⇒ अधिक $\\eta$ (जैसे ग्लिसरॉल, शहद)।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Both surface tension and viscosity for liquids **decrease** with temperature; vapour pressure **increases** with temperature.',
        hindi: 'तरल पदार्थों के लिए सतह तनाव और चिपचिपाहट दोनों तापमान के साथ **कमी** होती है; तापमान के साथ वाष्प का दबाव **बढ़ता** है।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'PV = nRT,\\ PM = dRT',
      desc: {
        english: 'Ideal gas equation',
        hindi: 'आदर्श गैस समीकरण',
      },
    },
    {
      latex: 'P_i = x_i P_{\\text{tot}}',
      desc: {
        english: 'Dalton\'s law',
        hindi: 'डाल्टन का नियम',
      },
    },
    {
      latex: 'r \\propto 1/\\sqrt M',
      desc: {
        english: 'Graham\'s law',
        hindi: 'ग्राहम का नियम',
      },
    },
    {
      latex: 'v_{rms} = \\sqrt{3RT/M}',
      desc: {
        english: 'RMS speed',
        hindi: 'आरएमएस गति',
      },
    },
    {
      latex: '\\bar E = \\tfrac32 k_B T\\ \\text{(per molecule)}',
      desc: {
        english: 'Avg KE',
        hindi: 'औसत केई',
      },
    },
    {
      latex: '(P + an^2/V^2)(V - nb) = nRT',
      desc: {
        english: 'van der Waals',
        hindi: 'वैन डेर वाल्स',
      },
    },
    {
      latex: 'T_c = 8a/(27Rb),\\ P_c = a/(27b^2),\\ V_c = 3b',
      desc: {
        english: 'Critical constants',
        hindi: 'गंभीर स्थिरांक',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Ideal gas',
      problem: {
        english: 'Density of CO₂ at STP (1 bar, 273 K)? (M = 44)',
        hindi: 'एसटीपी पर CO₂ का घनत्व (1 बार, 273 K)? (M = 44)',
      },
      answer: {
        english: '$\\approx 1.94$ g/L',
        hindi: '$\\approx 1.94$ g/L',
      },
      solution: {
        english: '$d = PM/(RT) = (1)(44)/(0.0831 \\cdot 273)$.',
        hindi: '$d = PM/(RT) = (1)(44)/(0.0831 \\cdot 273)$।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'KMT',
      problem: {
        english: 'Ratio of $v_{rms}$ of He to CH₄ at same $T$?',
        hindi: 'He के $v_{rms}$ से CH₄ का समान $T$ पर अनुपात?',
      },
      answer: {
        english: '$2 : 1$',
        hindi: '$2 : 1$',
      },
      solution: {
        english: '$\\sqrt{M_{CH_4}/M_{He}} = \\sqrt{16/4} = 2$.',
        hindi: '$\\sqrt{M_{CH_4}/M_{He}} = \\sqrt{16/4} = 2$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Real gas',
      problem: {
        english: 'For a vdW gas, units of $a$?',
        hindi: 'एक vdW गैस के लिए, $a$ की इकाइयाँ?',
      },
      answer: {
        english: 'L²·atm/mol²',
        hindi: 'L²·atm/mol²',
      },
      solution: {
        english: 'From $aN^2/V^2$ to give pressure (atm).',
        hindi: '$aN^2/V^2$ से दबाव (एटीएम) देने के लिए।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Graham',
      problem: {
        english: 'A gas diffuses 4× faster than O₂. Its molar mass?',
        hindi: 'एक गैस O₂ से 4× तेजी से फैलती है। इसका दाढ़ द्रव्यमान?',
      },
      answer: {
        english: '$2$ g/mol (H₂)',
        hindi: '$2$ g/mol (H₂)',
      },
      solution: {
        english: '$r_x/r_{O_2} = \\sqrt{32/M_x} = 4 \\Rightarrow M_x = 2$.',
        hindi: '$r_x/r_{O_2} = \\sqrt{32/M_x} = 4 \\Rightarrow M_x = 2$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Critical',
      problem: {
        english: 'For a vdW gas $a = 4$ atm·L²/mol², $b = 0.04$ L/mol. $T_c$ approximately? ($R = 0.0821$)',
        hindi: 'एक वीडीडब्ल्यू गैस के लिए $a = 4$ atm·L²/mol², $b = 0.04$ L/mol। $T_c$ लगभग? ($आर = 0.0821$)',
      },
      answer: {
        english: '$\\approx 361$ K',
        hindi: '$\\approx 361$ K',
      },
      solution: {
        english: '$T_c = 8a/(27Rb) = 32/(27 \\cdot 0.0821 \\cdot 0.04) \\approx 361$ K.',
        hindi: '$T_c = 8a/(27Rb) = 32/(27 \\cdot 0.0821 \\cdot 0.04) \\approx 361$ के.',
      },
    },
  ],
  next: {
    slug: 'thermodynamics-chem',
    title: 'Thermodynamics',
  },
};

export default chapter;
